import { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { MessageData, ChatUser } from './types';
import { getDisplayName, formatTime } from './utils';
import { ProfileImage } from '@/components/ProfileImage';
import outcloseLogo from '@/assets/icons/outclose_background.png';

const urlRegex = /(https?:\/\/[^\s]+)/g;

function renderTextWithLinks(text: string) {
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (part.match(/^https?:\/\//)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

interface MessageBubbleProps {
  message: MessageData;
  userId: string | null;
  userCache: Record<string, ChatUser>;
  // True when the next message is from a different sender (or there is no next
  // message). The avatar and the sender's name only render on the last bubble
  // in a contiguous run from the same author.
  isLastInGroup: boolean;
}

export function MessageBubble({
  message,
  userId,
  userCache,
  isLastInGroup,
}: MessageBubbleProps) {
  const isOwn = message.senderId === userId;
  const isSystem = message.type === 'system';
  const formattedContent = useMemo(() => renderTextWithLinks(message.content), [message.content]);

  const sender = userCache[message.senderId];
  const senderName = isSystem ? 'Outclose Team' : getDisplayName(message.senderId, userId, userCache);

  // Reserve the avatar gutter even when the avatar is hidden, so consecutive
  // bubbles from the same sender line up flush with the last one.
  const avatarGutter = !isOwn && (
    <div className="w-8 shrink-0 self-end">
      {isLastInGroup && (
        isSystem ? (
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center overflow-hidden">
            <img src={outcloseLogo} alt="Outclose" className="w-8 h-8 object-contain rounded-full" />
          </div>
        ) : (
          <ProfileImage
            size="sm"
            user={{
              firstName: sender?.firstName ?? sender?.name,
              lastName: sender?.lastName,
              profileImageUrl: sender?.profileImageUrl,
            }}
          />
        )
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      {avatarGutter}
      <div className={`max-w-[85%] flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-primary-950 text-white rounded-br-md'
              : isSystem
                ? 'bg-white dark:bg-neutral-900 border border-content rounded-bl-md'
                : 'bg-white dark:bg-neutral-900 border border-content rounded-bl-md'
          }`}
        >
          <p className="whitespace-pre-wrap break-words">{formattedContent}</p>
          <p
            className={`text-xs mt-1 ${
              isOwn ? 'text-primary-200' : 'text-dark-muted'
            }`}
          >
            {message.createdAt && formatTime(message.createdAt)}
          </p>
        </div>
        {isLastInGroup && !isOwn && (
          <p className="text-xs text-dark-muted mt-1">{senderName}</p>
        )}
      </div>
    </motion.div>
  );
}
